import { supabase } from "./churchSupabaseClient";

const SERMONS_TABLE = "church_sermons";
const SERVICE_ITEMS_TABLE = "service_items";
const SERMON_VISUAL_BUCKET = "church-media-library";
const SERMON_VISUAL_SIGNED_URL_SECONDS = 60 * 60 * 24 * 7;
const SERMON_VISUAL_DATA_URL_LIMIT = 6 * 1024 * 1024;

const SERMON_SETTINGS_KEY = "oikos.church.sermon.settings";
const DEFAULT_SERVICE_ID = "current_service";
const SERMON_ARCHIVE_KEY = "oikos.church.sermon.archive";

const defaultApiSettings = {
  apiKey: "EkDvO_NJOeoQaGesiYtLP",
  serviceId: DEFAULT_SERVICE_ID,
  translations: {
    NKJV: {
      bibleId: "63097d2a0a2f7db3-01",
      label: "NKJV",
    },
    KJV: {
      bibleId: "de4e12af7f28f599-01",
      label: "KJV",
    },
    NASB: {
      bibleId: "a761ca71e0b3ddcf-01",
      label: "NASB",
    },
  },
};

function isMissingRelationError(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("does not exist"))
  );
}

function sanitizeFileName(value = "image") {
  return String(value || "image")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "image";
}

function normalizeSermonOwner(owner) {
  if (owner && typeof owner === "object") {
    const userId = owner.userId || "";
    const orgUserId = owner.orgUserId || "";
    const accountId = owner.accountId || "";
    const sessionToken = owner.sessionToken || "";
    const storageKey =
      owner.storageKey ||
      (orgUserId ? `org.${orgUserId}` : userId ? `auth.${userId}` : "guest");

    return {
      userId,
      orgUserId,
      accountId,
      sessionToken,
      authorName: owner.authorName || "",
      storageKey,
      isOrgUser: Boolean(orgUserId && sessionToken),
    };
  }

  return {
    userId: owner || "",
    orgUserId: "",
    accountId: "",
    sessionToken: "",
    authorName: "",
    storageKey: owner ? `auth.${owner}` : "guest",
    isOrgUser: false,
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read this image."));
    reader.readAsDataURL(file);
  });
}

async function createVisualSlideDataUrl(file) {
  if (file?.size > SERMON_VISUAL_DATA_URL_LIMIT) {
    throw new Error("Choose an image under 6 MB, or sign in as an organization owner to upload larger images.");
  }

  return readFileAsDataUrl(file);
}

async function resolveSermonOwnerAccount(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (normalizedOwner.accountId || !normalizedOwner.userId) {
    return normalizedOwner;
  }

  try {
    const { data: ownedAccount, error: ownedError } = await supabase
      .from("accounts")
      .select("id")
      .eq("owner_user_id", normalizedOwner.userId)
      .eq("type", "church")
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (ownedError && !isMissingRelationError(ownedError)) {
      console.error("Sermon owner account lookup error:", ownedError);
    }

    if (ownedAccount?.id) {
      return {
        ...normalizedOwner,
        accountId: ownedAccount.id,
      };
    }

    const { data: membership, error: membershipError } = await supabase
      .from("account_members")
      .select("account_id")
      .eq("user_id", normalizedOwner.userId)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (membershipError && !isMissingRelationError(membershipError)) {
      console.error("Sermon owner membership lookup error:", membershipError);
    }

    if (membership?.account_id) {
      return {
        ...normalizedOwner,
        accountId: membership.account_id,
      };
    }
  } catch (error) {
    console.error("Sermon owner account resolve error:", error);
  }

  return normalizedOwner;
}

function getDraftStorageKey(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);
  return `oikos.church.sermon.draft.${normalizedOwner.storageKey}`;
}

function getArchiveStorageKey(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);
  return `${SERMON_ARCHIVE_KEY}.${normalizedOwner.storageKey}`;
}

function getServiceStorageKey(owner, serviceId) {
  const normalizedOwner = normalizeSermonOwner(owner);
  return `oikos.church.service-items.${normalizedOwner.storageKey}.${serviceId}`;
}

function getDraftTimestamp(draft = {}) {
  const timestamp = Date.parse(draft.updatedAt || draft.updated_at || "");
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function isLocalDraftPreferred(localDraft, supabaseDraft) {
  if (!localDraft) return false;
  if (!supabaseDraft) return true;

  if (localDraft.sourceSermonId || localDraft.sourceArchiveId) {
    return true;
  }

  return getDraftTimestamp(localDraft) >= getDraftTimestamp(supabaseDraft);
}

function loadDraftLocally(owner) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getDraftStorageKey(owner));

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return normalizeDraft(parsed);
  } catch (error) {
    console.error("Local sermon draft load error:", error);
    return null;
  }
}

function stripScriptureHeadings(value = "") {
  return String(value || "")
    .replace(/<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>/gi, " ")
    .replace(
      /<span\b[^>]*class=["'][^"']*(?:^|\s)(?:heading|title|section|s\d*)(?=\s|$)[^"']*["'][^>]*>[\s\S]*?<\/span>/gi,
      " "
    );
}

function stripHtml(value = "") {
  const withoutHeadings = stripScriptureHeadings(value);

  if (typeof window === "undefined") {
    return withoutHeadings.replace(/<[^>]*>/g, " ");
  }

  const container = window.document.createElement("div");
  container.innerHTML = withoutHeadings;
  return container.textContent || container.innerText || "";
}

function compactWhitespace(value = "") {
  return value
    .replace(/\s+/g, " ")
    .replace(/([.!?])(["'”’)]*)(?=[A-Z])/g, "$1$2 ")
    .replace(/([,;:])(?=\S)/g, "$1 ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

function getVerseNumberFromReference(reference = "") {
  const match = reference.match(/:(\d{1,3})(?:\s*[-–—]\s*\d{1,3})?\s*$/);
  return match ? match[1] : "";
}

function getRequestedVerseRange(reference = "") {
  const match = String(reference || "")
    .trim()
    .replace(/[–—]/g, "-")
    .match(/:(\d{1,3})(?:\s*-\s*(\d{1,3}))?\s*$/);

  if (!match) return null;

  const start = Number(match[1]);
  const end = Number(match[2] || match[1]);

  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  return {
    start: Math.min(start, end),
    end: Math.max(start, end),
  };
}

function isVerseInRequestedRange(verseNumber = "", requestedRange = null) {
  if (!requestedRange) return true;

  const numericVerse = Number(String(verseNumber || "").trim());
  return (
    Number.isFinite(numericVerse) &&
    numericVerse >= requestedRange.start &&
    numericVerse <= requestedRange.end
  );
}

function compareVerseSlides(left, right) {
  const leftNumber = Number(left?.verseNumber);
  const rightNumber = Number(right?.verseNumber);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return 0;
}

function markScriptureVerseBoundaries(content = "") {
  return String(content || "")
    .replace(
      /<([a-z][\w:-]*)\b([^>]*\bdata-(?:number|verse|verse-number)=["'](\d{1,3})["'][^>]*)>\s*(?:\d{1,3})?\s*<\/\1>/gi,
      " __OIKOS_VERSE_$3__ "
    )
    .replace(
      /<([a-z][\w:-]*)\b([^>]*\bclass=["'][^"']*\b(?:v|verse|verse-number)\b[^"']*["'][^>]*)>\s*(\d{1,3})\s*<\/\1>/gi,
      " __OIKOS_VERSE_$3__ "
    )
    .replace(
      /<([a-z][\w:-]*)\b([^>]*\bid=["'][A-Z0-9]+(?:\.[A-Z0-9]+)*\.(\d{1,3})["'][^>]*)>\s*(?:\d{1,3})?\s*<\/\1>/gi,
      " __OIKOS_VERSE_$3__ "
    )
    .replace(
      /<sup\b[^>]*>\s*(\d{1,3})\s*<\/sup>/gi,
      " __OIKOS_VERSE_$1__ "
    );
}

function stripLeadingVerseNumber(text = "", verseNumber = "") {
  if (!verseNumber) {
    return compactWhitespace(text);
  }

  const escapedVerseNumber = verseNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const withoutHeadingPrefix = text.replace(
    new RegExp(`^\\s*[^\\d\\n]{1,120}${escapedVerseNumber}\\s*`),
    ""
  );
  const withoutPrefix = text.replace(
    new RegExp(`^\\s*${escapedVerseNumber}\\s*`),
    ""
  );

  return compactWhitespace(
    withoutHeadingPrefix !== text ? withoutHeadingPrefix : withoutPrefix
  );
}

function getApiVerseNumber(verse = {}) {
  return (
    verse.verseNumber ||
    verse.number ||
    getVerseNumberFromReference(verse.reference || "") ||
    getVerseNumberFromReference(verse.orgId || "") ||
    ""
  );
}

function normalizeSlides(slides = []) {
  return slides.map((slide) => ({
    ...slide,
    enabled: slide.enabled !== false,
  }));
}

function normalizeItem(item = {}) {
  if (item.type === "scripture") {
    return {
      ...item,
      slides: normalizeSlides(Array.isArray(item.slides) ? item.slides : []),
    };
  }

  if (item.type === "custom" || item.type === "header") {
    return {
      ...item,
      includeInSlideshow: false,
    };
  }

  if (item.type === "text") {
    return {
      ...item,
      title: item.title || "Text Slide",
      body: item.body || "",
      includeInSlideshow: item.includeInSlideshow !== false,
    };
  }

  if (item.type === "visual") {
    return {
      ...item,
      title: item.title || "Visual Slide",
      imageUrl: item.imageUrl || "",
      imagePath: item.imagePath || "",
      storageBucket: item.storageBucket || SERMON_VISUAL_BUCKET,
      imageFit: item.imageFit || "contain",
      imagePositionX: Number.isFinite(Number(item.imagePositionX)) ? Number(item.imagePositionX) : 50,
      imagePositionY: Number.isFinite(Number(item.imagePositionY)) ? Number(item.imagePositionY) : 50,
      includeInSlideshow: item.includeInSlideshow !== false,
    };
  }

  return item;
}

function normalizeDraft(draft = {}) {
  return {
    ...getDefaultSermonDraft(),
    ...draft,
    titleSlideEnabled: draft.titleSlideEnabled !== false,
    items: Array.isArray(draft.items)
      ? draft.items.map((item) => normalizeItem(item))
      : [],
  };
}

function toStoredSermonPayload(draft, { owner, isActive, id } = {}) {
  const normalizedDraft = normalizeDraft(draft);
  const normalizedOwner = normalizeSermonOwner(owner);

  return {
    id: id || normalizedDraft.id,
    account_id: normalizedOwner.accountId || null,
    user_id: normalizedOwner.userId || null,
    org_user_id: normalizedOwner.orgUserId || null,
    author_name: normalizedOwner.authorName || "",
    mode: "church",
    title: normalizedDraft.title,
    speaker_name: normalizedDraft.speakerName,
    sermon_date: normalizedDraft.sermonDate,
    notes: normalizedDraft.notes,
    default_translation: normalizedDraft.defaultTranslation,
    items: normalizedDraft.items,
    is_active: isActive,
    updated_at: new Date().toISOString(),
  };
}

export function isSermonOwnedByOwner(owner, sermon = {}) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (normalizedOwner.isOrgUser) {
    return Boolean(
      normalizedOwner.orgUserId &&
        sermon.orgUserId &&
        sermon.orgUserId === normalizedOwner.orgUserId
    );
  }

  return Boolean(
    normalizedOwner.userId &&
      sermon.userId &&
      sermon.userId === normalizedOwner.userId
  );
}

function mapRowToDraft(data) {
  return normalizeDraft({
    id: data.id,
    archiveId: data.id,
    userId: data.user_id || data.userId || "",
    orgUserId: data.org_user_id || data.orgUserId || "",
    accountId: data.account_id || data.accountId || "",
    authorName: data.author_name || data.authorName || "",
    title: data.title || "",
    speakerName: data.speaker_name || "",
    sermonDate: data.sermon_date || new Date().toISOString().slice(0, 10),
    notes: data.notes || "",
    defaultTranslation: data.default_translation || "NKJV",
    items: Array.isArray(data.items) ? data.items : [],
    isActive: data.is_active !== false,
    updatedAt: data.updated_at || new Date().toISOString(),
  });
}

function getAuthorName(author = {}, fallback = "Unknown author") {
  return (
    author.full_name ||
    author.fullName ||
    author.email ||
    fallback
  );
}

async function hydrateSermonAuthors(sermons = []) {
  const authorIds = Array.from(
    new Set(sermons.map((sermon) => sermon.userId).filter(Boolean))
  );

  if (!authorIds.length) {
    return sermons.map((sermon) => ({
      ...sermon,
      authorName: sermon.authorName || "Unknown author",
    }));
  }

  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, email")
    .in("id", authorIds);

  if (error && !isMissingRelationError(error)) {
    console.error("Sermon author load error:", error);
  }

  const authorMap = new Map(
    (Array.isArray(data) ? data : []).map((profile) => [
      profile.id,
      getAuthorName(profile),
    ])
  );

  return sermons.map((sermon) => ({
    ...sermon,
    authorName:
      sermon.authorName || authorMap.get(sermon.userId) || "Unknown author",
  }));
}

function createVerseSlide(reference, verseNumber, text, index) {
  const suffix = verseNumber ? `:${verseNumber}` : ` #${index + 1}`;

  return {
    id: `${reference}-${verseNumber || index + 1}`,
    type: "verse-slide",
    title: `${reference}${suffix}`,
    reference,
    verseNumber: verseNumber || `${index + 1}`,
    text: stripLeadingVerseNumber(text, verseNumber),
  };
}

function parsePassageContent(reference, content = "", requestedReference = reference) {
  const requestedRange = getRequestedVerseRange(requestedReference);
  const markedContent = markScriptureVerseBoundaries(content);
  const plainText = compactWhitespace(stripHtml(markedContent));

  if (!plainText) {
    return [];
  }

  const markerMatches = [...plainText.matchAll(/__OIKOS_VERSE_(\d{1,3})__/g)];
  const matches = markerMatches.length > 0
    ? markerMatches
    : [...plainText.matchAll(/(?:^|\s)(\d{1,3})(?=\s|[A-Za-z"“'‘])/g)];

  if (matches.length <= 1) {
    const verseNumber = matches[0]?.[1] || getVerseNumberFromReference(requestedReference);

    if (!isVerseInRequestedRange(verseNumber, requestedRange)) {
      return [];
    }

    const text = matches[0]
      ? plainText.slice(matches[0].index + matches[0][0].length).trim()
      : plainText;

    return [
      createVerseSlide(requestedReference, verseNumber, text, 0),
    ];
  }

  return matches
    .map((match, index) => {
      const verseNumber = match[1];
      const start = match.index + match[0].length;
      const end =
        index + 1 < matches.length ? matches[index + 1].index : plainText.length;
      const text = plainText.slice(start, end).trim();

      return createVerseSlide(requestedReference, verseNumber, text, index);
    })
    .filter((slide) =>
      slide.text && isVerseInRequestedRange(slide.verseNumber, requestedRange)
    )
    .sort(compareVerseSlides);
}

function parseManualVerseText(reference, manualText = "") {
  const lines = manualText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return [];
  }

  return lines.map((line, index) => {
    const numberedMatch = line.match(/^(\d{1,3})[\s.:;-]+(.+)$/);

    if (numberedMatch) {
      return createVerseSlide(
        reference,
        numberedMatch[1],
        numberedMatch[2],
        index
      );
    }

    return createVerseSlide(reference, "", line, index);
  });
}

function normalizeSearchResult(reference, translationLabel, payload) {
  const data = payload?.data;
  const requestedRange = getRequestedVerseRange(reference);

  if (Array.isArray(data?.verses) && data.verses.length > 0) {
    const slides = data.verses
      .map((verse, index) => {
        const verseNumber = getApiVerseNumber(verse);
        return createVerseSlide(
          reference,
          verseNumber,
          verse.text || verse.content || "",
          index
        );
      })
      .filter((slide) =>
        slide.text && isVerseInRequestedRange(slide.verseNumber, requestedRange)
      )
      .sort(compareVerseSlides);

    if (slides.length > 0) {
      return slides;
    }
  }

  if (Array.isArray(data?.passages) && data.passages.length > 0) {
    const slides = data.passages.flatMap((passage) =>
      parsePassageContent(
        passage.reference || reference,
        passage.content || passage.text || "",
        reference
      )
    );

    if (slides.length > 0) {
      return slides;
    }
  }

  if (Array.isArray(data) && data.length > 0) {
    const slides = data.flatMap((item) => {
      if (!item?.content && !item?.text) {
        return [];
      }

      return parsePassageContent(
        item.reference || reference,
        item.content || item.text || "",
        reference
      );
    });

    if (slides.length > 0) {
      return slides;
    }
  }

  throw new Error(
    `No scripture content came back for ${reference} (${translationLabel}).`
  );
}

async function callOrgUserSermonRpc(functionName, owner, params = {}) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (!normalizedOwner.isOrgUser) {
    return null;
  }

  const { data, error } = await supabase.rpc(functionName, {
    p_session_token: normalizedOwner.sessionToken,
    ...params,
  });

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error(`${functionName} error:`, error);
    }

    return null;
  }

  return data;
}

async function tryLoadDraftFromSupabase(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (normalizedOwner.isOrgUser) {
    const data = await callOrgUserSermonRpc(
      "church_org_user_list_sermons",
      normalizedOwner,
      {
        p_scope: "mine",
        p_is_active: true,
      }
    );
    const row = Array.isArray(data) ? data[0] : null;
    return row ? mapRowToDraft(row) : null;
  }

  if (!normalizedOwner.userId) {
    return null;
  }

  const { data, error } = await supabase
    .from(SERMONS_TABLE)
    .select("*")
    .eq("user_id", normalizedOwner.userId)
    .eq("mode", "church")
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Sermon draft load error:", error);
    }

    return null;
  }

  if (!data) {
    return null;
  }

  return mapRowToDraft(data);
}

async function saveDraftToSupabase(owner, draft) {
  const normalizedOwner = await resolveSermonOwnerAccount(owner);

  if (normalizedOwner.isOrgUser) {
    await callOrgUserSermonRpc("church_org_user_save_sermon", normalizedOwner, {
      p_sermon_id: draft.id,
      p_title: draft.title || "",
      p_speaker_name: draft.speakerName || "",
      p_sermon_date: draft.sermonDate || null,
      p_notes: draft.notes || "",
      p_default_translation: draft.defaultTranslation || "NKJV",
      p_items: draft.items || [],
      p_is_active: true,
    });
    return;
  }

  if (!normalizedOwner.userId) {
    return;
  }

  const payload = toStoredSermonPayload(draft, {
    owner: normalizedOwner,
    isActive: true,
  });

  const { error } = await supabase
    .from(SERMONS_TABLE)
    .upsert(payload, { onConflict: "id" });

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }
  }
}

async function archiveDraftToSupabase(owner, draft, archiveId = crypto.randomUUID()) {
  const normalizedOwner = await resolveSermonOwnerAccount(owner);

  if (normalizedOwner.isOrgUser) {
    await callOrgUserSermonRpc("church_org_user_save_sermon", normalizedOwner, {
      p_sermon_id: archiveId,
      p_title: draft.title || "",
      p_speaker_name: draft.speakerName || "",
      p_sermon_date: draft.sermonDate || null,
      p_notes: draft.notes || "",
      p_default_translation: draft.defaultTranslation || "NKJV",
      p_items: draft.items || [],
      p_is_active: false,
    });
    return;
  }

  if (!normalizedOwner.userId) {
    return;
  }

  const payload = toStoredSermonPayload(draft, {
    owner: normalizedOwner,
    id: archiveId,
    isActive: false,
  });

  const { error } = await supabase
    .from(SERMONS_TABLE)
    .upsert(payload, { onConflict: "id" });

  if (error && !isMissingRelationError(error)) {
    throw error;
  }
}

function saveArchivedDraftLocally(userId, draft, archiveId = crypto.randomUUID()) {
  if (typeof window === "undefined") {
    return;
  }

  const archiveKey = getArchiveStorageKey(userId);
  const nextArchiveEntry = {
    ...normalizeDraft(draft),
    id: archiveId,
    archiveId,
    archivedAt: new Date().toISOString(),
  };

  try {
    const raw = window.localStorage.getItem(archiveKey);
    const currentEntries = raw ? JSON.parse(raw) : [];
    const nextEntries = [
      nextArchiveEntry,
      ...(Array.isArray(currentEntries) ? currentEntries : []).filter(
        (entry) =>
          entry.id !== archiveId &&
          entry.archiveId !== archiveId &&
          entry.sourceSermonId !== archiveId
      ),
    ].slice(0, 30);
    window.localStorage.setItem(archiveKey, JSON.stringify(nextEntries));
  } catch (error) {
    console.error("Local sermon archive save error:", error);
  }
}

async function tryListPastSermonsFromSupabase(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (normalizedOwner.isOrgUser) {
    const data = await callOrgUserSermonRpc(
      "church_org_user_list_sermons",
      normalizedOwner,
      {
        p_scope: "mine",
        p_is_active: false,
      }
    );

    return Array.isArray(data) ? data.map((row) => mapRowToDraft(row)) : [];
  }

  if (!normalizedOwner.userId) {
    return null;
  }

  const { data, error } = await supabase
    .from(SERMONS_TABLE)
    .select("*")
    .eq("user_id", normalizedOwner.userId)
    .eq("mode", "church")
    .eq("is_active", false)
    .order("updated_at", { ascending: false })
    .limit(20);

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Past sermons load error:", error);
    }

    return null;
  }

  return Array.isArray(data) ? data.map((row) => mapRowToDraft(row)) : [];
}

async function tryListSharedSermonsFromSupabase(owner) {
  const normalizedOwner = await resolveSermonOwnerAccount(owner);

  if (normalizedOwner.isOrgUser) {
    const data = await callOrgUserSermonRpc(
      "church_org_user_list_sermons",
      normalizedOwner,
      {
        p_scope: "shared",
        p_is_active: false,
      }
    );
    const sermons = Array.isArray(data) ? data.map((row) => mapRowToDraft(row)) : [];
    return hydrateSermonAuthors(sermons);
  }

  if (!normalizedOwner.userId) {
    return null;
  }

  let query = supabase
    .from(SERMONS_TABLE)
    .select("*")
    .eq("mode", "church")
    .eq("is_active", false)
    .order("updated_at", { ascending: false })
    .limit(100);

  if (normalizedOwner.accountId) {
    query = query.eq("account_id", normalizedOwner.accountId);
  }

  const { data, error } = await query.or(
    `user_id.is.null,user_id.neq.${normalizedOwner.userId}`
  );

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Shared sermons load error:", error);
    }

    return null;
  }

  const sermons = Array.isArray(data) ? data.map((row) => mapRowToDraft(row)) : [];
  return hydrateSermonAuthors(sermons);
}

function listPastSermonsLocally(userId) {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(getArchiveStorageKey(userId));

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((entry) => normalizeDraft(entry))
      : [];
  } catch (error) {
    console.error("Local past sermons load error:", error);
    return [];
  }
}

async function pushServiceItemsToSupabase(serviceId, serviceItems) {
  const { error: deleteError } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .delete()
    .eq("service_id", serviceId)
    .eq("source_app", "sermon");

  if (deleteError && !isMissingRelationError(deleteError)) {
    throw deleteError;
  }

  const { error: insertError } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(serviceItems);

  if (insertError && !isMissingRelationError(insertError)) {
    throw insertError;
  }
}

async function deleteSermonFromSupabase(owner, sermonId) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (normalizedOwner.isOrgUser) {
    await callOrgUserSermonRpc("church_org_user_delete_sermon", normalizedOwner, {
      p_sermon_id: sermonId,
    });
    return;
  }

  const { error } = await supabase.from(SERMONS_TABLE).delete().eq("id", sermonId);

  if (error && !isMissingRelationError(error)) {
    throw error;
  }
}

export function getDefaultSermonDraft() {
  return {
    id: crypto.randomUUID(),
    title: "",
    speakerName: "",
    sermonDate: new Date().toISOString().slice(0, 10),
    notes: "",
    defaultTranslation: "NKJV",
    titleSlideEnabled: true,
    items: [],
    updatedAt: new Date().toISOString(),
  };
}

export function createCustomSlideItem() {
  return {
    id: crypto.randomUUID(),
    type: "custom",
    title: "Note Block",
    body: "",
    includeInSlideshow: false,
  };
}

export function createHeaderBlockItem() {
  return {
    id: crypto.randomUUID(),
    type: "header",
    title: "Section Header",
    includeInSlideshow: false,
  };
}

export function createVisualSlideItem() {
  return {
    id: crypto.randomUUID(),
    type: "visual",
    title: "Visual Slide",
    caption: "",
    imageUrl: "",
    imagePath: "",
    storageBucket: SERMON_VISUAL_BUCKET,
    imageFit: "contain",
    imagePositionX: 50,
    imagePositionY: 50,
    includeInSlideshow: true,
  };
}

export function createTextSlideItem() {
  return {
    id: crypto.randomUUID(),
    type: "text",
    title: "Text Slide",
    body: "",
    includeInSlideshow: true,
  };
}

export async function getSermonVisualSlideImageUrl(item = {}) {
  if (item.imagePath) {
    const { data, error } = await supabase.storage
      .from(item.storageBucket || SERMON_VISUAL_BUCKET)
      .createSignedUrl(item.imagePath, SERMON_VISUAL_SIGNED_URL_SECONDS);

    if (!error && data?.signedUrl) {
      return data.signedUrl;
    }
  }

  return item.imageUrl || "";
}

export async function uploadSermonVisualSlideImage(owner, itemId, file) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Choose an image file for this visual slide.");
  }

  const normalizedOwner = await resolveSermonOwnerAccount(owner);
  const safeItemId = itemId || crypto.randomUUID();
  const safeFileName = sanitizeFileName(file.name || "visual-slide.png");

  if (normalizedOwner.accountId && normalizedOwner.userId) {
    const filePath = `${normalizedOwner.accountId}/sermons/visual-slides/${safeItemId}/${Date.now()}-${safeFileName}`;
    const { error: uploadError } = await supabase.storage
      .from(SERMON_VISUAL_BUCKET)
      .upload(filePath, file, {
        cacheControl: "3600",
        contentType: file.type || "image/png",
        upsert: false,
      });

    if (!uploadError) {
      const { data, error: signedError } = await supabase.storage
        .from(SERMON_VISUAL_BUCKET)
        .createSignedUrl(filePath, SERMON_VISUAL_SIGNED_URL_SECONDS);

      if (!signedError && data?.signedUrl) {
        return {
          imageUrl: data.signedUrl,
          imagePath: filePath,
          storageBucket: SERMON_VISUAL_BUCKET,
        };
      }
    }
  }

  return {
    imageUrl: await createVisualSlideDataUrl(file),
    imagePath: "",
    storageBucket: "",
  };
}

export function createDraftFromSermon(sourceDraft) {
  const normalized = normalizeDraft(sourceDraft);
  const sourceSermonId = sourceDraft?.id || normalized.id || "";
  const sourceArchiveId = sourceDraft?.archiveId || normalized.archiveId || "";

  return {
    ...normalized,
    id: crypto.randomUUID(),
    sourceSermonId,
    sourceArchiveId,
    archiveId: undefined,
    updatedAt: new Date().toISOString(),
  };
}

export function buildTitleSlide(draft) {
  return {
    id: "title-slide",
    type: "title",
    title: draft.title || "Untitled Sermon",
    speakerName: draft.speakerName || "",
    sermonDate: draft.sermonDate,
    includeInSlideshow: draft.titleSlideEnabled !== false,
    slides: [
      {
        id: "title-slide-main",
        type: "title-slide",
        title: draft.title || "Untitled Sermon",
        subtitle: draft.speakerName || "",
        date: draft.sermonDate,
        enabled: draft.titleSlideEnabled !== false,
      },
    ],
  };
}

export function buildServiceItemsFromDraft(draft, serviceId) {
  const normalizedDraft = normalizeDraft(draft);
  const sermonItems = [buildTitleSlide(normalizedDraft), ...normalizedDraft.items]
    .map((item) => {
      if (item.type === "scripture") {
        const enabledSlides = (item.slides || []).filter(
          (slide) => slide.enabled !== false
        );

        return enabledSlides.length > 0
          ? {
              ...item,
              slides: enabledSlides,
            }
          : null;
      }

      if (item.type === "custom" || item.type === "header") {
        return null;
      }

      if (item.type === "text") {
        return item.title || item.body ? item : null;
      }

      if (item.type === "visual") {
        return item.imageUrl || item.imagePath ? item : null;
      }

      if (item.type === "title") {
        const enabledSlides = (item.slides || []).filter(
          (slide) => slide.enabled !== false
        );

        return enabledSlides.length > 0
          ? {
              ...item,
              slides: enabledSlides,
            }
          : null;
      }

      return item;
    })
    .filter(Boolean);

  return sermonItems.map((item, index) => ({
    id: crypto.randomUUID(),
    service_id: serviceId,
    type:
      item.type === "scripture"
        ? "verse"
        : item.type === "custom"
          ? "custom_slide"
          : item.type === "visual"
            ? "visual_slide"
            : item.type === "text"
              ? "custom_slide"
              : "title_slide",
    source_app: "sermon",
    source_id: item.id,
    sort_order: index + 1,
    title:
      item.type === "scripture"
        ? item.reference
        : item.title || normalizedDraft.title || "Sermon Item",
    payload: {
      ...item,
      sermonTitle: normalizedDraft.title,
      slides: item.slides || [],
    },
    created_at: new Date().toISOString(),
  }));
}

export function getSermonApiSettings() {
  if (typeof window === "undefined") {
    return defaultApiSettings;
  }

  try {
    const raw = window.localStorage.getItem(SERMON_SETTINGS_KEY);

    if (!raw) {
      return defaultApiSettings;
    }

    const parsed = JSON.parse(raw);

    return {
      ...defaultApiSettings,
      ...parsed,
      translations: {
        ...defaultApiSettings.translations,
        ...parsed.translations,
      },
    };
  } catch (error) {
    console.error("Sermon settings load error:", error);
    return defaultApiSettings;
  }
}

export function saveSermonApiSettings(settings) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    SERMON_SETTINGS_KEY,
    JSON.stringify({
      ...defaultApiSettings,
      ...settings,
      translations: {
        ...defaultApiSettings.translations,
        ...settings.translations,
      },
    })
  );
}

export async function loadSermonDraft(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const localDraft = loadDraftLocally(normalizedOwner);
  const supabaseDraft =
    normalizedOwner.userId || normalizedOwner.isOrgUser
      ? await tryLoadDraftFromSupabase(normalizedOwner)
      : null;

  if (isLocalDraftPreferred(localDraft, supabaseDraft)) {
    return localDraft;
  }

  if (supabaseDraft) {
    return supabaseDraft;
  }

  return localDraft || getDefaultSermonDraft();
}

export async function saveSermonDraft(owner, draft) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const nextDraft = normalizeDraft({
    ...draft,
    updatedAt: new Date().toISOString(),
  });

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      getDraftStorageKey(normalizedOwner),
      JSON.stringify(nextDraft)
    );
  }

  if ((normalizedOwner.userId || normalizedOwner.isOrgUser) && !nextDraft.sourceSermonId) {
    await saveDraftToSupabase(normalizedOwner, nextDraft);
  }

  return nextDraft;
}

export async function saveSermonLive(owner, draft) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const nextDraft = normalizeDraft({
    ...draft,
    updatedAt: new Date().toISOString(),
  });
  const shouldUpdateSourceSermon =
    Boolean(nextDraft.sourceSermonId) && isSermonOwnedByOwner(normalizedOwner, nextDraft);

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      getDraftStorageKey(normalizedOwner),
      JSON.stringify(nextDraft)
    );
  }

  if (shouldUpdateSourceSermon) {
    saveArchivedDraftLocally(normalizedOwner, nextDraft, nextDraft.sourceSermonId);

    if (normalizedOwner.userId || normalizedOwner.isOrgUser) {
      await archiveDraftToSupabase(normalizedOwner, nextDraft, nextDraft.sourceSermonId);
    }

    return nextDraft;
  }

  return saveSermonDraft(normalizedOwner, nextDraft);
}

export async function saveSermonToLibrary(owner, draft, options = {}) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const nextDraft = normalizeDraft({
    ...draft,
    updatedAt: new Date().toISOString(),
  });
  const shouldUpdateSourceSermon =
    Boolean(nextDraft.sourceSermonId) && isSermonOwnedByOwner(normalizedOwner, nextDraft);

  if (nextDraft.sourceSermonId && !shouldUpdateSourceSermon && options.saveSharedCopy === false) {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(getDraftStorageKey(normalizedOwner));
    }

    return nextDraft;
  }

  const archiveId = shouldUpdateSourceSermon ? nextDraft.sourceSermonId : crypto.randomUUID();

  saveArchivedDraftLocally(normalizedOwner, nextDraft, archiveId);

  if (normalizedOwner.userId || normalizedOwner.isOrgUser) {
    await archiveDraftToSupabase(normalizedOwner, nextDraft, archiveId);

    if (nextDraft.id && nextDraft.id !== archiveId) {
      await deleteSermonFromSupabase(normalizedOwner, nextDraft.id);
    }
  }

  if (typeof window !== "undefined") {
    window.localStorage.removeItem(getDraftStorageKey(normalizedOwner));
  }

  return nextDraft;
}

export async function pushSermonToService(owner, draft) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const settings = getSermonApiSettings();
  const serviceId = settings.serviceId || DEFAULT_SERVICE_ID;
  const serviceItems = buildServiceItemsFromDraft(draft, serviceId);

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      getServiceStorageKey(normalizedOwner, serviceId),
      JSON.stringify(serviceItems)
    );
  }

  await pushServiceItemsToSupabase(serviceId, serviceItems);
  saveArchivedDraftLocally(normalizedOwner, draft);

  if (normalizedOwner.userId || normalizedOwner.isOrgUser) {
    await archiveDraftToSupabase(normalizedOwner, draft);
  }

  return {
    serviceId,
    itemCount: serviceItems.length,
  };
}

export async function listPastSermons(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);
  const supabaseSermons = normalizedOwner.userId || normalizedOwner.isOrgUser
    ? await tryListPastSermonsFromSupabase(normalizedOwner)
    : null;

  if (supabaseSermons) {
    return supabaseSermons;
  }

  return listPastSermonsLocally(normalizedOwner);
}

export async function listSharedSermons(owner) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (!normalizedOwner.userId && !normalizedOwner.isOrgUser) {
    return [];
  }

  const supabaseSermons = await tryListSharedSermonsFromSupabase(normalizedOwner);

  return supabaseSermons || [];
}

export async function deleteSermon(owner, sermon) {
  const normalizedOwner = normalizeSermonOwner(owner);

  if (!sermon) {
    return;
  }

  if (typeof window !== "undefined") {
    if (sermon.isDraft) {
      window.localStorage.removeItem(getDraftStorageKey(normalizedOwner));
    } else {
      try {
        const raw = window.localStorage.getItem(getArchiveStorageKey(normalizedOwner));
        const currentEntries = raw ? JSON.parse(raw) : [];
        const nextEntries = Array.isArray(currentEntries)
          ? currentEntries.filter(
              (entry) =>
                entry.archiveId !== sermon.archiveId &&
                entry.id !== sermon.id
            )
          : [];

        window.localStorage.setItem(
          getArchiveStorageKey(normalizedOwner),
          JSON.stringify(nextEntries)
        );
      } catch (error) {
        console.error("Local sermon delete error:", error);
      }
    }
  }

  if ((normalizedOwner.userId || normalizedOwner.isOrgUser) && sermon.id) {
    await deleteSermonFromSupabase(normalizedOwner, sermon.id);
  }
}

export async function fetchScriptureSlides({
  reference,
  translationKey = "NKJV",
  manualText = "",
}) {
  const settings = getSermonApiSettings();
  const translationConfig = settings.translations?.[translationKey];
  const apiKey = settings.apiKey?.trim();
  const bibleId = translationConfig?.bibleId?.trim();
  const normalizedReference = reference.trim().replace(/[–—]/g, "-");

  if (apiKey && bibleId) {
    const searchUrl = new URL(
      `https://rest.api.bible/v1/bibles/${bibleId}/search`
    );

    searchUrl.searchParams.set("query", normalizedReference);
    searchUrl.searchParams.set("limit", "50");

    const response = await fetch(searchUrl.toString(), {
      headers: {
        "api-key": apiKey,
      },
    });

    if (response.ok) {
      const payload = await response.json();
      const slides = normalizeSearchResult(reference, translationKey, payload);

      return {
        reference,
        translationKey,
        slides,
        resolution: "api",
      };
    }

    const errorText = await response.text();

    if (manualText.trim()) {
      return {
        reference,
        translationKey,
        slides: parseManualVerseText(reference, manualText),
        resolution: "manual-fallback",
        warning: `API lookup failed and fallback text was used instead: ${errorText}`,
      };
    }

    throw new Error(
      `Scripture lookup failed for ${translationKey}. ${
        response.status
      } ${errorText || ""}`.trim()
    );
  }

  if (manualText.trim()) {
    return {
      reference,
      translationKey,
      slides: parseManualVerseText(reference, manualText),
      resolution: "manual-fallback",
      warning:
        "API key or Bible ID is missing for this translation, so fallback text was used.",
    };
  }

  throw new Error(
    `Missing API settings for ${translationKey}. Add an API key and Bible ID in Sermon Settings, or paste fallback text.`
  );
}
